import {
  ProductNutritionPanels,
  ProductNutritionItems,
} from 'lib/services/models/product-response';

export interface INormalizedProductNutritionData {
  servingsPer: string | null;
  servingSize: string | null;
  servingHeader1: string | null;
  calories: string | number | null;
  nutritionItems?: Array<{
    displayName: string;
    quantity: string;
    percent: string;
    rowType: string;
  }>;
  vitaminItems?: Array<{
    displayName: string;
    quantity: string;
    percent: string;
  }>;
}

function getPanelSignature(panel: INormalizedProductNutritionData): string {
  return JSON.stringify({
    servingsPer: panel.servingsPer,
    servingSize: panel.servingSize,
    servingHeader1: panel.servingHeader1,
    calories: panel.calories,
    nutritionItems: panel.nutritionItems ?? [],
    vitaminItems: panel.vitaminItems ?? [],
  });
}

function normalizeServingSizeKey(servingSize: string | null): string | null {
  // Ignore whitespace/case so "100 g" and "100g" group together.
  return servingSize != null ? servingSize.replace(/\s+/g, '').toLowerCase() : null;
}

function mergeProductPanels(panels: ProductNutritionItems[]): ProductNutritionItems[] {
  const groupedPanels = new Map<string, ProductNutritionItems[]>();

  panels.forEach((panel, index) => {
    const key = normalizeServingSizeKey(panel.servingSize) ?? `panel-${panel.panelIndex ?? index}`;
    const group = groupedPanels.get(key);
    if (group) {
      group.push(panel);
    } else {
      groupedPanels.set(key, [panel]);
    }
  });

  // For duplicate serving sizes, pick the record that has calories.
  return Array.from(groupedPanels.values()).map(
    (group) => group.find((panel) => panel.calories != null) ?? group[0]
  );
}

export function normalizeProductNutritionData(
  productData: ProductNutritionPanels,
  getDictionaryValue: (key: string) => string,
  isGMFS = false
): INormalizedProductNutritionData[] {
  if (!productData?.panels?.length) return [];

  // GMFS merges split serving-size records; other sites filter by calories.
  const panelsToNormalize = isGMFS
    ? mergeProductPanels(productData.panels)
    : productData.panels.filter((panel) => panel.calories);

  const normalizedProductData: INormalizedProductNutritionData[] = panelsToNormalize.map(
    (panel) => ({
      servingsPer:
        panel.servingsPer && String(panel.servingsPer).match(/\d+/g) !== null
          ? panel.servingsPer
          : null,
      servingSize: panel.servingSize || null,
      servingHeader1: panel.servingHeader1 || null,
      calories: panel.calories || null,
      nutritionItems: restructureProductNutritionData(
        panel as ProductNutritionItems,
        getDictionaryValue
      ).filter(
        (
          item
        ): item is { displayName: string; quantity: string; percent: string; rowType: string } =>
          item !== null
      ),
      vitaminItems: restructureProductVitaminData(
        panel as ProductNutritionItems,
        getDictionaryValue
      ).filter(
        (item): item is { displayName: string; quantity: string; percent: string } => item !== null
      ),
    })
  );

  const seen = new Set<string>();
  return normalizedProductData.filter((panel) => {
    const signature = getPanelSignature(panel);
    if (seen.has(signature)) {
      return false;
    }

    seen.add(signature);
    return true;
  });
}

function restructureProductNutritionData(
  nutritionData: ProductNutritionItems,
  getDictionaryValue: (key: string) => string
) {
  // Nutrition lists to extract from PIM response
  // Unit values must be g, mg, or mcg
  // rowType values must be 'primary', 'subitem', or 'addedsugars'
  const nutritionItemsList = [
    {
      baseName: 'totalFat',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.TotalFat'),
      unit: 'g',
      rowType: 'primary',
    },
    {
      baseName: 'saturatedFat',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.SaturatedFat'),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'transFat',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.TransFat'),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'polyunsaturatedFat',
      displayName: getDictionaryValue(
        'Common.NutritionFacts.Product.NutrientNames.PolyunsaturatedFat'
      ),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'monounsaturatedFat',
      displayName: getDictionaryValue(
        'Common.NutritionFacts.Product.NutrientNames.MonounsaturatedFat'
      ),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'cholesterol',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.Cholesterol'),
      unit: 'mg',
      rowType: 'primary',
    },
    {
      baseName: 'sodium',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.Sodium'),
      unit: 'mg',
      rowType: 'primary',
    },
    {
      baseName: 'totalCarbohydrate',
      displayName: getDictionaryValue(
        'Common.NutritionFacts.Product.NutrientNames.TotalCarbohydrate'
      ),
      unit: 'g',
      rowType: 'primary',
    },
    {
      baseName: 'dietaryFiber',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.DietaryFiber'),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'solubleFiber',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.SolubleFiber'),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'sugar',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.TotalSugars'),
      unit: 'g',
      rowType: 'subitem',
    },
    {
      baseName: 'addedSugar',
      displayName: getDictionaryValue(
        'Common.NutritionFacts.Product.NutrientNames.InclAddedSugars'
      ),
      unit: 'g',
      rowType: 'addedsugars',
    },
    {
      baseName: 'protein',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.Protein'),
      unit: 'g',
      rowType: 'primary',
    },
  ];

  // Get new nutrition structure
  const restructuredNutritionData = nutritionItemsList
    .map((item) => {
      // Get units
      const unitAbbr = item.unit.toLowerCase();
      const unitName = getFullUnitName(unitAbbr);

      // Generate Property Names to match Product Service API format
      // Note: Total sugars doesn't follow the same naming pattern in the API as the rest of the items.
      let quantityPropertyName = item.baseName + unitName;
      const percentagePropertyName =
        item.baseName === 'sugar' ? 'totalSugarPercent' : item.baseName + 'Percent';

      // Check if LessThan value exists.
      // The primary value and LessThan value are separated by different properties in the API
      // If a LessThan value does exist, use the LessThan property name instead of the primary property name.
      let lessThanIndicator = '';
      const hasLessThan =
        nutritionData[quantityPropertyName + 'LessThan'] &&
        nutritionData[quantityPropertyName + 'LessThan'] !== null
          ? true
          : false;
      if (hasLessThan) {
        quantityPropertyName = item.baseName + unitName + 'LessThan';
        lessThanIndicator = '<';
      }

      // Get Quantity and Percent Values from API response.
      // Prepend any LessThan values with '<', and apped percent values with '%'.
      const quantityValue =
        nutritionData[quantityPropertyName] !== undefined &&
        nutritionData[quantityPropertyName] !== null
          ? String(lessThanIndicator + nutritionData[quantityPropertyName] + unitAbbr)
          : false;
      const percentageValue =
        nutritionData[percentagePropertyName] !== undefined &&
        nutritionData[percentagePropertyName] !== null
          ? String(nutritionData[percentagePropertyName] + '%')
          : false;

      // Create Restructured Values
      let restructuredValues = null;
      if (quantityValue) {
        restructuredValues = {
          displayName: item.displayName,
          quantity: quantityValue,
          percent: percentageValue || '',
          rowType: item.rowType,
        };
      }

      return restructuredValues;
    })
    .filter((item) => item !== null);

  return restructuredNutritionData;
}

function restructureProductVitaminData(
  vitaminData: ProductNutritionItems,
  getDictionaryValue: (key: string) => string
) {
  if (!vitaminData) {
    return [];
  }
  // Vitamin lists to extract from Product response - Unit value must be g, mg, or mcg
  const vitaminItemsList = [
    {
      baseName: 'vitaminD',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminD'),
      unit: 'mcg',
    },
    {
      baseName: 'calcium',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Calcium'),
      unit: 'mg',
    },
    {
      baseName: 'iron',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Iron'),
      unit: 'mg',
    },
    {
      baseName: 'potassium',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Potassium'),
      unit: 'mg',
    },
    {
      baseName: 'vitaminA',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminA'),
      unit: 'mcg',
    },
    {
      baseName: 'vitaminC',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminC'),
      unit: 'mg',
    },
    {
      baseName: 'vitaminE',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminE'),
      unit: 'mg',
    },
    {
      baseName: 'vitaminK',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminK'),
      unit: 'mcg',
    },
    {
      baseName: 'thiamin',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Thiamin'),
      unit: 'mg',
    },
    {
      baseName: 'riboflavin',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Riboflavin'),
      unit: 'mg',
    },
    {
      baseName: 'niacin',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Niacin'),
      unit: 'mg',
    },
    {
      baseName: 'vitaminB6',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminB6'),
      unit: 'mg',
    },
    {
      baseName: 'folate',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Folate'),
      unit: null,
    },
    {
      baseName: 'folicAcid',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.FolicAcid'),
      unit: 'mcg',
    },
    {
      baseName: 'vitaminB12',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.VitaminB12'),
      unit: 'mcg',
    },
    {
      baseName: 'pantothenate',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Pantothenate'),
      unit: 'mcg',
    },
    {
      baseName: 'pantothenicAcid',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.PantothenicAcid'),
      unit: 'mg',
    },
    {
      baseName: 'phosphorus',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Phosphorus'),
      unit: 'mg',
    },
    {
      baseName: 'magnesium',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Magnesium'),
      unit: 'mg',
    },
    {
      baseName: 'zinc',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Zinc'),
      unit: 'mg',
    },
    {
      baseName: 'copper',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Copper'),
      unit: 'mg',
    },
    {
      baseName: 'manganese',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Manganese'),
      unit: 'mg',
    },
    {
      baseName: 'water',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Water'),
      unit: 'g',
    },
    {
      baseName: 'ash',
      displayName: getDictionaryValue('Common.NutritionFacts.Product.VitaminNames.Ash'),
      unit: 'g',
    },
  ];

  // Get new vitamin structure
  const restructuredVitaminData = vitaminItemsList
    .map((item) => {
      // TypeScript: vitaminData is guaranteed non-null here
      const data = vitaminData;

      const quantityKey = item.unit
        ? item.baseName + getFullUnitName(item.unit.toLowerCase())
        : item.baseName;
      const quantityRaw = data?.[quantityKey];
      const quantityValue =
        quantityRaw != null
          ? String(quantityRaw) + (item.unit ? item.unit.toLowerCase() : '')
          : false;

      const percentKey = item.baseName + 'Percent';
      const percentRaw = data?.[percentKey];
      const percentageValue = percentRaw != null ? String(percentRaw) + '%' : false;

      // Create Restructured Values
      let restructuredValues = null;
      if (quantityValue || percentageValue) {
        restructuredValues = {
          displayName: item.displayName || '',
          quantity: quantityValue || '',
          percent: percentageValue || '',
        };
      }

      return restructuredValues;
    })
    .filter((item) => item !== null);

  return restructuredVitaminData;
}

function getFullUnitName(unit: string | null) {
  switch (unit) {
    case 'g':
      unit = 'Grams';
      break;
    case 'mg':
      unit = 'Milligrams';
      break;
    case 'mcg':
      unit = 'Micrograms';
      break;
    default:
      unit = '';
  }
  return unit;
}
